/**
 * Emitter MCP Tools — deterministic file generators (swp-zf9y pattern)
 *
 * Each tool in this file is a thin bridge between:
 *   1. Project config files (read from disk)
 *   2. A pure emitter function (from @stackwright-pro/emitters)
 *   3. The otter (receives the generated content, writes via safe_write)
 *
 * The otter does NOT compose the file contents. The emitter is the source of
 * truth. This is the swp-zf9y architectural principle — infrastructure files
 * emit deterministically from typed inputs, not from LLM prompt-templates.
 *
 * Tools in this file:
 *   stackwright_pro_emit_env_local       — .env.local + .env.local.example (swp-8qdd)
 *   stackwright_pro_emit_providers_file  — app/_components/providers.tsx   (swp-57ku)
 *   stackwright_pro_emit_mock_ports      — mock backend port assignment     (swp-phkh)
 *
 * Refs: swp-8qdd (first emitter), swp-57ku (providers emitter), swp-phkh (ports emitter),
 *        swp-zf9y (architecture pattern)
 */
import { McpServer } from '@modelcontextprotocol/sdk/server/mcp.js';
import { z } from 'zod';
import { readFileSync, existsSync, readdirSync } from 'fs';
import { join } from 'path';
import yaml from 'js-yaml';
import { emitEnvLocal, emitProvidersFile, emitMockPorts } from '@stackwright-pro/emitters';
import type { IntegrationConfig, ServiceTokenRef } from '@stackwright-pro/emitters';

// ---------------------------------------------------------------------------
// YAML shape helpers — minimal parsing, no heavy schema validation
// ---------------------------------------------------------------------------

/** Raw shape of one entry in stackwright.integrations.yml */
interface RawIntegration {
  name?: string;
  auth?: {
    type?: string;
    header?: string;
    envVar?: string;
  };
}

/** Raw shape of stackwright.integrations.yml */
interface RawIntegrationsFile {
  integrations?: RawIntegration[];
}

/** Raw shape of a services/*.yml file */
interface RawServiceFile {
  name?: string;
  tokenEnv?: string;
}

// ---------------------------------------------------------------------------
// Readers — parse project files into emitter inputs
// ---------------------------------------------------------------------------

/**
 * Read and parse stackwright.integrations.yml.
 * Returns an empty list if the file is missing or malformed.
 */
function readIntegrations(cwd: string): IntegrationConfig[] {
  const filePath = join(cwd, 'stackwright.integrations.yml');
  if (!existsSync(filePath)) return [];
  try {
    const raw = yaml.load(readFileSync(filePath, 'utf-8')) as RawIntegrationsFile | null;
    if (!raw?.integrations || !Array.isArray(raw.integrations)) return [];
    return raw.integrations
      .filter((i): i is RawIntegration & { name: string } => typeof i.name === 'string')
      .map((i) => ({
        name: i.name,
        auth: i.auth
          ? { type: i.auth.type, header: i.auth.header, envVar: i.auth.envVar }
          : undefined,
      }));
  } catch {
    // Malformed YAML — fall through with empty list, don't crash the otter
    return [];
  }
}

/**
 * Scan services/*.yml for tokenEnv declarations.
 * Returns an empty list if the services/ directory is missing.
 */
function readServiceTokenRefs(cwd: string): ServiceTokenRef[] {
  const servicesDir = join(cwd, 'services');
  if (!existsSync(servicesDir)) return [];
  try {
    const files = readdirSync(servicesDir).filter((f) => f.endsWith('.yml') || f.endsWith('.yaml'));
    const refs: ServiceTokenRef[] = [];
    for (const file of files) {
      try {
        const raw = yaml.load(
          readFileSync(join(servicesDir, file), 'utf-8')
        ) as RawServiceFile | null;
        if (raw?.tokenEnv && typeof raw.tokenEnv === 'string') {
          refs.push({
            name: raw.name ?? file.replace(/\.ya?ml$/, ''),
            tokenEnv: raw.tokenEnv,
          });
        }
      } catch {
        // Skip malformed service YAML — don't crash; just omit this service's tokenEnv
      }
    }
    return refs;
  } catch {
    return [];
  }
}

// ---------------------------------------------------------------------------
// Tool registration
// ---------------------------------------------------------------------------

const ENV_LOCAL_DESC =
  'Returns { envLocal, envLocalExample } as JSON. The otter writes both files via stackwright_pro_safe_write.';

const PROVIDERS_DESC =
  'Returns { providersFile } as JSON. The otter writes the file via stackwright_pro_safe_write.';

const PORTS_DESC = 'Returns { ports } as JSON — map of integration name → port number.';

export function registerEmitterTools(server: McpServer): void {
  server.tool(
    'stackwright_pro_emit_env_local',
    `Emit .env.local + .env.local.example contents from stackwright.integrations.yml and ` +
      `services/*.yml. Deterministic emitter — same project config produces byte-identical output. ` +
      `\n\nCall this tool, then write both files with stackwright_pro_safe_write:\n` +
      `  1. stackwright_pro_emit_env_local({ cwd }) → { envLocal, envLocalExample }\n` +
      `  2. stackwright_pro_safe_write({ filePath: '.env.local', content: envLocal, ... })\n` +
      `  3. stackwright_pro_safe_write({ filePath: '.env.local.example', content: envLocalExample, ... })\n\n` +
      `Do NOT compose the contents by hand. This emitter is the source of truth. ` +
      `If an env var is missing, update integrations.yml or the emitter — not the file. ` +
      `This is the first concrete instance of the swp-zf9y deterministic emitter pattern. ${ENV_LOCAL_DESC}`,
    {
      cwd: z
        .string()
        .describe(
          'Absolute path to the project root containing stackwright.integrations.yml and optionally services/*.yml'
        ),
    },
    async ({ cwd }) => {
      const integrations = readIntegrations(cwd);
      const services = readServiceTokenRefs(cwd);
      const output = emitEnvLocal({ integrations, services });
      return {
        content: [
          {
            type: 'text' as const,
            text: JSON.stringify(
              {
                envLocal: output.envLocal,
                envLocalExample: output.envLocalExample,
                meta: {
                  integrationsRead: integrations.length,
                  serviceTokenRefsRead: services.length,
                  envVarsEmitted: (output.envLocal.match(/^NEXT_PUBLIC_[A-Z0-9_]+=.+$/m)
                    ? (output.envLocal.match(/^NEXT_PUBLIC_[A-Z0-9_]+=.+$/gm) ?? [])
                    : []
                  ).length,
                },
              },
              null,
              2
            ),
          },
        ],
      };
    }
  );

  server.tool(
    'stackwright_pro_emit_providers_file',
    `Emit app/_components/providers.tsx from stackwright.integrations.yml. ` +
      `Deterministic emitter — uses LITERAL env access per integration so Next.js can inline ` +
      `NEXT_PUBLIC_* vars at build time. Also registers the map provider (default: Cesium for Pro). ` +
      `\n\n⚠️ CRITICAL: DO NOT compose providers.tsx by hand. Computed env access ` +
      `(process.env[varName]) returns undefined in browser bundles for NEXT_PUBLIC_* vars — ` +
      `this silently drops all auth headers and causes 401s on every integration.` +
      `\n\nWorkflow:\n` +
      `  1. stackwright_pro_emit_providers_file({ cwd, mapProvider: 'cesium' }) → { providersFile }\n` +
      `  2. stackwright_pro_safe_write({ filePath: 'app/_components/providers.tsx', content: providersFile, ... })\n\n` +
      `mapProvider options:\n` +
      `  - 'cesium' (default for Pro): 3D globe + terrain, @stackwright-pro/cesium + cesium peer dep\n` +
      `  - 'maplibre': free-tier 2D maps, no API key — OSS-style 2D dashboards only (opt-out)\n` +
      `  - 'none': skip map registration (ONLY if no pages use map content_items)\n\n` +
      `This is the second concrete instance of the swp-zf9y deterministic emitter pattern. ` +
      `Refs: swp-57ku, swp-2m89, swp-zf9y, drawer 957. ${PROVIDERS_DESC}`,
    {
      cwd: z
        .string()
        .describe('Absolute path to the project root containing stackwright.integrations.yml'),
      mapProvider: z
        .enum(['maplibre', 'cesium', 'none'])
        .optional()
        .describe(
          'Map provider to register (default: cesium for Pro — 3D globe + terrain). ' +
            'Use maplibre for OSS-style 2D dashboards (explicit opt-out). ' +
            'Use none if pages have no map content_items.'
        ),
    },
    async ({ cwd, mapProvider }) => {
      const integrations = readIntegrations(cwd);
      const providersFile = emitProvidersFile({ integrations, mapProvider });
      return {
        content: [
          {
            type: 'text' as const,
            text: JSON.stringify(
              {
                providersFile,
                meta: {
                  integrationsRead: integrations.length,
                  integrationsWithAuth: integrations.filter((i) => i.auth?.envVar).length,
                  mapProvider: mapProvider ?? 'cesium',
                },
              },
              null,
              2
            ),
          },
        ],
      };
    }
  );

  server.tool(
    'stackwright_pro_emit_mock_ports',
    `Emit mock backend port assignments from stackwright.integrations.yml. ` +
      `Deterministic emitter — assigns consecutive ports starting at 4011 to each integration ` +
      `in declaration order. Skipped integrations (skipMockPort=true) are omitted and do not ` +
      `consume a port number.` +
      `\n\n⚠️ DO NOT pick mockUrl port numbers yourself. Port collisions occur when different ` +
      `models pick different defaults (Opus always picked 4010 for all integrations in run-4).` +
      `\n\nWorkflow:\n` +
      `  1. stackwright_pro_emit_mock_ports({ cwd }) → { ports }\n` +
      `  2. For each integration, set mockUrl to http://localhost:<ports[name]>\n` +
      `  3. Write stackwright.integrations.yml via stackwright_pro_safe_write\n\n` +
      `This is the third concrete instance of the swp-zf9y deterministic emitter pattern. ` +
      `Refs: swp-phkh, swp-zf9y. ${PORTS_DESC}`,
    {
      cwd: z
        .string()
        .describe('Absolute path to the project root containing stackwright.integrations.yml'),
    },
    async ({ cwd }) => {
      const integrations = readIntegrations(cwd);
      const { ports } = emitMockPorts({ integrations });
      return {
        content: [
          {
            type: 'text' as const,
            text: JSON.stringify(
              {
                ports,
                meta: {
                  integrationsRead: integrations.length,
                  portsAssigned: Object.keys(ports).length,
                  portRange:
                    Object.keys(ports).length > 0
                      ? `${Math.min(...Object.values(ports))}–${Math.max(...Object.values(ports))}`
                      : 'none',
                },
              },
              null,
              2
            ),
          },
        ],
      };
    }
  );
}
